'use client';

import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ProjectFilters component
 * Displays a list of category filters for projects
 * Enhanced with animations and styling consistent with the rest of the portfolio
 */
export interface ProjectFiltersProps {
  categories: string[];
  activeFilter: string | null;
  onFilterChange: (category: string | null) => void;
  className?: string;
}

export function ProjectFilters({
  categories,
  activeFilter,
  onFilterChange,
  className
}: ProjectFiltersProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" });
    }
  }, []);

  const getCategoryLabel = (categoryValue: string): string => {
    return t(`Projects.categories.${categoryValue}` as any) || categoryValue;
  };

  return (
    <div
      ref={containerRef}
      className={cn('space-y-3', className)}
    >
      <div
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Icon
            icon="carbon:filter"
            className="h-4 w-4 text-slate-600 dark:text-slate-400"
          />
          <h3 className="text-sm font-medium text-slate-900 dark:text-slate-200">{t('Projects.filter')}</h3>
        </div>

        {activeFilter && (
          <button
            onClick={() => onFilterChange(null)}
            className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors hover:scale-105 active:scale-95"
          >
            {t('Projects.clearFilter')}
          </button>
        )}
      </div>

      <div
        className="flex flex-wrap gap-2"
      >
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => onFilterChange(activeFilter === category ? null : category)}
            className={cn(
              'px-2 py-1 text-xs rounded-md transition-all hover:scale-105 active:scale-95',
              activeFilter === category
                ? 'bg-slate-700 dark:bg-slate-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700/50 hover:-translate-y-0.5'
            )}
          >
            {getCategoryLabel(category)}
          </button>
        ))}
      </div>
    </div>
  );
} 